import { useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createColumnHelper } from '@tanstack/react-table'
import { coatTypeDeleteMutation, coatTypeIndexOptions, coatTypeIndexQueryKey } from '$api-client/@tanstack/react-query.gen'
import { CoatType } from '$api-client/types.gen'
import { AnimatedOutlet, Breadcrumb, Button, ConfirmationModal, ElementIntent, GenericTable, OptionsCell, Text, TextCell, useModalState, usePageTitle } from '$components'
import { BinIcon, EditIcon, PlusIcon, SettingsIcon } from '$components'
import { ToastHelper, ToastType } from '$components'
import { SettingsRoutePath } from '$features/settings/routes/protected'
import { Router } from '$utils/router'
import { TabLayout } from '$features/settings/layouts/tab'

const columnHelper = createColumnHelper<CoatType>()

export function CoatsPage() {
  usePageTitle('Settings - Coat Types')

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { modalState, open, close } = useModalState()

  const { data: coats } = useQuery({
    ...coatTypeIndexOptions(),
  })

  const { mutate: deleteCoat, isPending: isDeleting } = useMutation({
    ...coatTypeDeleteMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: coatTypeIndexQueryKey() })
      ToastHelper.deleteSuccess(ToastType.CoatType)
      close()
    },
    onError: () => ToastHelper.deleteError(ToastType.CoatType),
  })

  const columns = useMemo(() => [
    columnHelper.accessor(row => row.values?.name, {
      id: 'name',
      header: 'Name',
      cell: info => <TextCell>{info.getValue()}</TextCell>,
    }),
    columnHelper.display({
      id: 'actions',
      maxSize: 66,
      minSize: 66,
      cell: ({ row }) => (
        <OptionsCell
          options={[
            {
              id: 'edit',
              value: 'Edit',
              icon: EditIcon,
              onClick: () => navigate(Router.buildRoute(SettingsRoutePath.Settings, SettingsRoutePath.SettingsPetOptions, Router.getRoute(SettingsRoutePath.SettingsCoatTypeUpdate, { coatTypeId: row.original.id }))),
            },
            {
              id: 'delete',
              value: 'Delete',
              intent: ElementIntent.Danger,
              icon: BinIcon,
              onClick: () => open(row.original.id),
            },
          ]}
        />
      ),
    }),
  ], [navigate, open])

  return (
    <TabLayout>
      <Breadcrumb
        items={[
          {
            label: 'Settings',
            icon: SettingsIcon,
            path: SettingsRoutePath.Settings,
          },
          {
            label: 'Coat Types',
            path: Router.buildRoute(SettingsRoutePath.Settings, SettingsRoutePath.SettingsPetOptionsCoats),
          },
        ]}
      />

      <div className="flex items-center justify-between">
        <div>
          <Text
            type="h2"
            weight="medium"
          >
            Coat Types
          </Text>

          <Text
            type="s"
            className="text-fg-subtle mt-stack-dense"
          >
            Define coat types used for service pricing and pet profiles.
          </Text>
        </div>

        <Button
          variant="primary"
          icon={PlusIcon}
          onClick={() => navigate(Router.buildRoute(SettingsRoutePath.Settings, SettingsRoutePath.SettingsPetOptions, 'coats/create'))}
        >
          Add Coat Type
        </Button>
      </div>

      {coats?.data && coats.data.length > 0
        ? (
          <div>
            <GenericTable.Container>
              <GenericTable
                data={coats.data}
                columns={columns}
              />
            </GenericTable.Container>
          </div>
        )
        : (
          <Text
            type="s"
            className="text-fg-subtle"
          >
            No coat types added yet.
          </Text>
        )}

      {modalState.isOpen && (
        <ConfirmationModal
          isLoading={isDeleting}
          intent={ElementIntent.Danger}
          onClose={close}
          onConfirm={() => deleteCoat({ path: { coatType: modalState.modelId } })}
          title="Delete Coat Type"
          description="Are you sure you want to delete this coat type? This may affect existing services and pets."
        />
      )}

      <AnimatedOutlet />
    </TabLayout>
  )
}
